import { useState, type FormEvent } from "react";
import { Link, useLocation } from "wouter";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  LockKeyhole,
  ShieldCheck,
  KeyRound,
  RefreshCw,
} from "lucide-react";

import { Logo } from "@/components/bits";
import { PasswordInput } from "@/components/password-input";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, publicApi } from "@/services/api-client";
import { useAuth } from "./auth-context";
import { useI18n, LanguageSwitcher } from "@/i18n/i18n-context";

export function LoginPage() {
  const { t } = useI18n();
  const { login } = useAuth();
  const [, navigate] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accountId, setAccountId] = useState("");
  const [accountType, setAccountType] = useState<
    "" | "INDIVIDUAL" | "BUSINESS"
  >("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // 2FA state
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorChannel, setTwoFactorChannel] = useState("EMAIL");
  const [twoFactorDestination, setTwoFactorDestination] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [resending, setResending] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setSuccessMsg("");
    setSubmitting(true);

    try {
      await login({
        email: email.trim(),
        password,
        ...(twoFactorRequired && twoFactorCode.trim()
          ? { two_factor_code: twoFactorCode.trim() }
          : {}),
        ...(accountId.trim() ? { account_id: accountId.trim() } : {}),
        ...(accountType ? { account_type: accountType } : {}),
      });
    } catch (caught) {
      if (
        caught instanceof ApiError &&
        caught.status === 403 &&
        caught.message.toLowerCase().includes("verify your email")
      ) {
        navigate(`/verify-email?email=${encodeURIComponent(email.trim())}`);
        return;
      }

      if (
        caught instanceof ApiError &&
        (caught.code === "TWO_FACTOR_REQUIRED" ||
          caught.message.toLowerCase().includes("two-factor"))
      ) {
        setTwoFactorRequired(true);
        const details = (caught.details as Record<string, string>) || {};
        setTwoFactorChannel(details.channel || "EMAIL");
        setTwoFactorDestination(details.destination || email.trim());
        setError("");
        return;
      }

      setError(
        caught instanceof ApiError
          ? caught.message
          : "La connexion est temporairement indisponible.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function resendOtp() {
    setResending(true);
    setError("");
    setSuccessMsg("");

    try {
      await publicApi("/auth/two-factor/resend-otp", {
        method: "POST",
        body: { email: email.trim() },
      });
      setSuccessMsg(t("auth.twoFactorResentToast"));
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Impossible de renvoyer le code pour le moment.",
      );
    } finally {
      setResending(false);
    }
  }

  return (
    <main className="relative grid min-h-[100dvh] bg-[#f4f7fb] lg:grid-cols-[1.05fr_0.95fr]">
      <div className="absolute right-4 top-4 z-20">
        <LanguageSwitcher variant="auth" />
      </div>

      <section className="relative hidden overflow-hidden bg-[#061a45] p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="absolute -right-40 -top-40 h-96 w-96 rounded-full bg-cyan-400/15 blur-3xl" />
        <div className="absolute -bottom-32 left-20 h-80 w-80 rounded-full bg-emerald-400/15 blur-3xl" />
        <Logo light />
        <div className="relative max-w-xl">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-200">
            <ShieldCheck className="h-4 w-4" /> {t("auth.loginBadge")}
          </p>
          <h1 className="text-5xl font-black leading-[1.08] tracking-tight">
            {t("auth.loginHeroTitle")}
          </h1>
          <p className="mt-6 max-w-lg text-base leading-7 text-slate-300">
            {t("auth.loginHeroSub")}
          </p>
        </div>
        <p className="relative text-xs text-slate-400">
          {t("auth.loginHeroFooter")}
        </p>
      </section>

      <section className="flex items-center justify-center px-5 py-10 sm:px-10">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <Logo />
          </div>

          {!twoFactorRequired ? (
            <>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
                {t("auth.loginCategory")}
              </p>
              <h2 className="mt-3 text-3xl font-black tracking-tight text-[#0b1f4b]">
                {t("auth.loginWelcome")}
              </h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {t("auth.loginSub")}
              </p>

              <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
                {error && (
                  <div
                    role="alert"
                    className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
                  >
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email">{t("auth.emailLabel")}</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={t("auth.emailPlaceholder")}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">{t("auth.passwordLabel")}</Label>
                    <Link
                      href="/forgot-password"
                      data-testid="link-forgot-password"
                      className="text-xs font-semibold text-primary hover:underline"
                    >
                      {t("auth.forgotPasswordLink")}
                    </Link>
                  </div>
                  <PasswordInput
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder={t("auth.passwordPlaceholder")}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="button-login"
                  className="h-11 w-full gap-2"
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <LockKeyhole className="h-4 w-4" />
                  )}
                  {submitting ? t("auth.signingIn") : t("auth.signInBtn")}
                  {!submitting && <ArrowRight className="ml-auto h-4 w-4" />}
                </Button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-[#f4f7fb] px-2 text-muted-foreground">
                      {t("auth.orDivider")}
                    </span>
                  </div>
                </div>
              </form>

              <div className="mt-8 flex flex-col items-center gap-2 text-center text-sm text-muted-foreground">
                <p>
                  {t("auth.noAccount")}{" "}
                  <Link
                    href="/register"
                    className="font-semibold text-primary hover:underline"
                  >
                    {t("auth.registerLink")}
                  </Link>
                </p>
                <Link
                  href="/verify-email"
                  className="text-xs text-primary hover:underline"
                >
                  {t("auth.resendVerificationLink")}
                </Link>
              </div>
            </>
          ) : (
            <div className="space-y-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
                  {t("auth.twoFactorTitle")}
                </p>
                <h2 className="mt-3 text-2xl font-black tracking-tight text-[#0b1f4b]">
                  {t("auth.twoFactorTitle")}
                </h2>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  {t("auth.twoFactorSub")
                    .replace("{channel}", twoFactorChannel)
                    .replace(
                      "{destination}",
                      twoFactorDestination || email.trim(),
                    )}
                </p>
              </div>

              {error && (
                <div
                  role="alert"
                  className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
                >
                  {error}
                </div>
              )}

              {successMsg && (
                <div
                  role="status"
                  className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700"
                >
                  {successMsg}
                </div>
              )}

              <form onSubmit={submit} className="space-y-4" noValidate>
                <div className="space-y-2">
                  <Label htmlFor="two_factor_code">
                    {t("auth.twoFactorCodeLabel")}
                  </Label>
                  <Input
                    id="two_factor_code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={32}
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value)}
                    placeholder={t("auth.twoFactorPlaceholder")}
                    className="h-12 text-center text-lg font-mono tracking-widest"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="button-verify-2fa"
                  className="h-11 w-full gap-2"
                  disabled={submitting || !twoFactorCode.trim()}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <KeyRound className="h-4 w-4" />
                  )}
                  {t("auth.twoFactorVerifyBtn")}
                </Button>
              </form>

              <div className="flex flex-col gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={resending}
                  onClick={() => void resendOtp()}
                  className="w-full gap-2 text-xs"
                >
                  {resending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  {t("auth.twoFactorResendBtn")}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setTwoFactorRequired(false);
                    setTwoFactorCode("");
                    setError("");
                  }}
                  className="w-full gap-2 text-xs text-muted-foreground"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t("auth.twoFactorBackBtn")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
